import React, { useEffect, useRef, useState } from "react";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import styles from "./Transcript.module.css";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useRegistrationInfo from "../../../hooks/useRegistrationInfo";
import useOrganizationSetting from "../../../hooks/useOrganizationSetting";
import { useUIContext } from "../../../context/UIContext";
import { useAuthContext } from "../../../context/AuthContext";
import { generateAllRegisteredStudents } from "../../../util/dealingArrObj";
import { checkHrteachers } from "../../../util/checkPermissions";
import { useReactToPrint } from "react-to-print";
import LoadingInside from "../../../unicomponents/LoadingInside";
import LoadingModal from "../../../unicomponents/LoadingModal";
import SimpleAlertMessageBlock from "../../../unicomponents/SimpleAlertMessageBlock";
import { getAppliedPeriods } from "../../../util/dateRelated";
import useLessonStudents from "../../../hooks/useLessonStudentData";
import useSchoolYearPeriod from "../../../hooks/useSchoolYearPeriod";
import useAttendance from "../../../hooks/useAttendance";
import HavenTranscript from "./transcriptsBySchools/HavenTranscript";
import DisplayControlBar from "./transcriptsBySchools/DisplayControlBar";

export default function Transcript() {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];
  const [filters, setFilters] = useState({
    schoolYear: "",
    schoolYearId: "",
    selectedStudent: "",
  });
  const [registrationArray, setRegistrationArray] = useState([]);
  const [keyArray, setKeyArray] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [studentList, setStudentList] = useState("");
  const [usernameList, setUsernameList] = useState([]);
  const [gradeRows, setGradeRows] = useState([]);
  // const [validTermNames, setValidTermNames] = useState([]);

  const [attendanceRows, setAttendanceRows] = useState([]);

  // 출력 제어 설정 상태 (기본값: 모든 학기 visible=true, courseTitleOnly=false)
  const [displaySettings, setDisplaySettings] = useState({
    "9-1": { visible: true, courseTitleOnly: false },
    "9-2": { visible: true, courseTitleOnly: false },
    "10-1": { visible: true, courseTitleOnly: false },
    "10-2": { visible: true, courseTitleOnly: false },
    "11-1": { visible: true, courseTitleOnly: false },
    "11-2": { visible: true, courseTitleOnly: false },
    "12-1": { visible: true, courseTitleOnly: false },
    "12-2": { visible: true, courseTitleOnly: false },
  });
  const [showControlBar, setShowControlBar] = useState(false);
  const { attendanceQuery } = useAttendance();
  const attendanceQueryRef = useRef(attendanceQuery);

  const {
    registrationInfoQuery: { isLoading, data: registrationInfoData },
    hrTeachersQuery: { data: hrTeachersData },
  } = useRegistrationInfo({ schoolid: schoolId }, { hrTeachers: true });

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting({ schoolid: schoolId });

  const { getAllLessonStudentsByStudentId } = useLessonStudents();
  const getAllLessonStudentsByStudentIdRef = useRef(
    getAllLessonStudentsByStudentId
  );

  const {
    schoolYearPeriodQuery: { data: schoolYearPeriodData },
  } = useSchoolYearPeriod({ schoolid: schoolId });

  const { handleToastCenterTop, handleToastCenterBottom } = useUIContext();
  const { hasPermission } = useAuthContext();

  // 서버 데이터 로딩을 중앙에서 추적 (mutation + query 합계)
  const fetchingCount = useIsFetching();
  const mutatingCount = useIsMutating();
  const isBusy = fetchingCount + mutatingCount > 0;

  const [dataReady, setDataReady] = useState(true);
  const hasSeenBusyRef = useRef(false);
  const settleTimerRef = useRef(null);

  // keyArray 변경(학생 선택) 시 데이터 로딩 시작으로 간주
  useEffect(() => {
    if (!keyArray || keyArray.length === 0) {
      setDataReady(true);
      return;
    }
    hasSeenBusyRef.current = false;
    setDataReady(false);
  }, [keyArray]);

  // isBusy 전이 감시: 로딩이 settle되면 dataReady true
  useEffect(() => {
    if (dataReady) return;
    if (isBusy) {
      hasSeenBusyRef.current = true;
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
      return;
    }
    // isBusy === 0: wave 사이 gap(짧음)과 첫 wave 대기(김)를 구분
    const delay = hasSeenBusyRef.current ? 500 : 1500;
    settleTimerRef.current = setTimeout(() => setDataReady(true), delay);
    return () => settleTimerRef.current && clearTimeout(settleTimerRef.current);
  }, [isBusy, dataReady]);

  useEffect(() => {
    //초기에 학생과 과정 username, fullname, 학년도가 있는 row가 되도로 개향 한다.
    if (settingData && settingData.length > 0 && registrationInfoData) {
      //학생 리스트
      let finalArray;
      finalArray = generateAllRegisteredStudents(
        registrationInfoData,
        settingData
      );

      setRegistrationArray(finalArray);
      // setStudentList(finalArray);

      if (hasPermission(null, "can_read")) {
        setStudentList(finalArray);
      } else if (checkHrteachers(hrTeachersData)) {
        const filteredRows = finalArray.filter((r) =>
          hrTeachersData.find((data) => data.username === r.username)
        );
        setStudentList(filteredRows);

        handleToastCenterBottom(t("common.messages.homeroomReadOnly"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationInfoData, settingData, hrTeachersData, hasPermission]);

  useEffect(() => {
    if (keyArray && keyArray.length > 0 && schoolYearPeriodData) {
      const forWhere = {
        schoolid: keyArray[0].schoolid,
        username: keyArray[0].username,
      };

      const appliedSchoolYears = getAppliedPeriods(
        keyArray,
        schoolYearPeriodData
      );

      getAllLessonStudentsByStudentIdRef.current.mutate(forWhere, {
        onSuccess: (data) => {
          const sortedData = processAndSortData(
            data,
            appliedSchoolYears,
            keyArray
          );

          setGradeRows(sortedData);
          // Extract unique term names
          // const uniqueTerms = [
          //   ...new Set(
          //     sortedData.map((row) => row.lesson?.TermDates.term_name)
          //   ),
          // ].sort();
          // setValidTermNames(uniqueTerms);
        },
        onError: (error) => {
          console.error(error); // 에러 출력
          alert(t("common.messages.errorAlert") + error.message);
        },
      });
    }
  }, [keyArray, schoolYearPeriodData, t]);

  useEffect(() => {
    if (keyArray && keyArray.length > 0 && schoolYearPeriodData) {
      // 전체 출석 정보를 한 번에 가져오기
      const appliedSchoolYears = getAppliedPeriods(
        keyArray,
        schoolYearPeriodData
      );

      // console.log("appliedSchoolYears", appliedSchoolYears);

      const startdate = appliedSchoolYears[0]?.start_date;
      const enddate =
        appliedSchoolYears[appliedSchoolYears.length - 1]?.end_date;

      const forWhere = {
        schoolid: keyArray[0]?.schoolid,
        username: keyArray[0]?.username,
        startdate,
        enddate,
      };

      // 전체 출석 정보 가져오기
      attendanceQueryRef.current.mutate(forWhere, {
        onSuccess: (data) => {
          if (data && data.length > 0) {
            setAttendanceRows(data);
          }
        },
        onError: (error) => {
          console.error(error); // 일단 콘솔에 에러를 출력합니다.
          alert(t("common.messages.errorAlert") + error.message);
        },
      });
    } else {
      setAttendanceRows([]);
    }
  }, [keyArray, schoolYearPeriodData, t]);

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchKey(value);
    if (value === "") {
      setUsernameList([]);
    } else if (studentList) {
      const filteredRows = studentList.filter((row) =>
        row.fullname.toLowerCase().includes(value.toLowerCase())
      );

      // 고유한 username을 기준으로 중복 제거
      const uniqueFilteredRows = Array.from(
        new Map(filteredRows.map((item) => [item.username, item])).values()
      );

      // 고유한 항목으로 setUsernameList 업데이트
      setUsernameList(uniqueFilteredRows);
    }
  };

  const handleSelectUsername = (e) => {
    const { value } = e.target;
    // console.log("value", value);
    setFilters((filters) => ({
      ...filters,
      selectedStudent: value,
    }));

    const filteredArray = registrationArray.filter(
      (row) => row.username.toString() === value
    );

    const makeArray = filteredArray.sort((a, b) => {
      if (a.schoolYearDates?.length > 0 && b.schoolYearDates?.length > 0) {
        const aStartDate = new Date(a.schoolYearDates[0].start_date);
        const bStartDate = new Date(b.schoolYearDates[0].start_date);
        return bStartDate - aStartDate;
      }
      return b.school_year - a.school_year;
    });

    // console.log("makeArray", makeArray);
    setKeyArray(makeArray);
  };

  const triggerPrint = useReactToPrint({
    content: () => {
      // addPageNumbers();
      return contentToPrint.current;
    },
    documentTitle: `${keyArray[0]?.["1"] || ""}-Transcript-${
      keyArray[0]?.fullname
    }`,
    onBeforePrint: () => {
      const content = contentToPrint.current;
      if (content) {
        content.style.width = "297mm";
        content.style.height = "auto";
      }
      console.log("before printing...");
    },
    removeAfterPrint: true,
    pageStyle: `

          @page {
            size: A4 landscape;
            margin: 10mm 10mm 10mm 10mm;
                  }

    @media print {

          body {
            counter-reset: page;
          }

          .print-page {
            counter-increment: page;
          }

          thead {
            display: table-row-group;
          }

          tfoot {
            display: table-row-group;
          }

        }
        `,
  });

  const contentToPrint = useRef(null);

  const isDataLoading = !dataReady && keyArray.length > 0;

  const handlePrint = () => {
    if (isDataLoading) {
      handleToastCenterTop("데이터를 불러오는 중입니다. 잠시만 기다려주세요.");
      return;
    }
    if (filters.selectedStudent) {
      triggerPrint();
    } else {
      console.log(
        "keyArray is incomplete. Please ensure all data is available."
      );
      handleToastCenterTop(t("common.messages.printAfterSelectStudent"));
    }
  };

  if (isLoading) {
    return <LoadingInside />;
  }

  if (!hasPermission(null, "can_access", checkHrteachers(hrTeachersData))) {
    return (
      <SimpleAlertMessageBlock
        message={
          t("common.messages.homeroomAccessOnly") ||
          "담임 또는 접근 권한이 있는 사람만 접근할 수 있습니다."
        }
      />
    );
  }

  function processAndSortData(data, appliedSchoolYears, keyArray) {
    // 필터링된 연도 범위 계산
    const startYear = appliedSchoolYears[0]?.school_year;
    const endYear =
      appliedSchoolYears[appliedSchoolYears.length - 1]?.school_year;

    // 데이터 필터링
    const filteredData = data.filter((row) => {
      const schoolYear = parseInt(row.school_year, 10);
      return schoolYear >= Number(startYear) && schoolYear <= Number(endYear);
    });

    // 학년 정보 추가
    const withPeriodInfo = filteredData.map((row) => {
      const matchingStudentYear = keyArray.find((key) => {
        const paths = key.organization_path.split("/");
        return paths[1] === row.school_year_id.toString();
      });

      return {
        ...row,
        studentYear: matchingStudentYear ? matchingStudentYear[2] : "", // 학년 정보 추가, 없으면 빈 문자열
      };
    });

    // 데이터 정렬
    const sortedData = withPeriodInfo.sort((a, b) => {
      // 5. school_year로 정렬
      const aSchoolYear = parseInt(a.school_year, 10);
      const bSchoolYear = parseInt(b.school_year, 10);
      const schoolYearComparison = aSchoolYear - bSchoolYear;
      if (schoolYearComparison !== 0) return schoolYearComparison;

      // 4. TermDates.start_date로 정렬
      const aStartDate = new Date(
        a.lesson?.TermDates?.start_date || "2100-01-01"
      );
      const bStartDate = new Date(
        b.lesson?.TermDates?.start_date || "2100-01-01"
      );
      const startDateComparison = aStartDate - bStartDate;
      if (startDateComparison !== 0) return startDateComparison;

      // 3. extraAttributes.subjectOrder가 존재하면 이 값으로 정렬
      const aOrder = a.extraAttributes?.subjectOrder || Number.MAX_VALUE;
      const bOrder = b.extraAttributes?.subjectOrder || Number.MAX_VALUE;
      const orderComparison = parseInt(aOrder, 10) - parseInt(bOrder, 10);
      if (orderComparison !== 0) return orderComparison;

      // 2. extraAttributes.subjectCategory가 존재하면 이 값으로 정렬
      const aCategory = a.extraAttributes?.subjectCategory || "";
      const bCategory = b.extraAttributes?.subjectCategory || "";
      const categoryComparison = aCategory.localeCompare(bCategory);
      if (categoryComparison !== 0) return categoryComparison;

      // 1. subjectName으로 정렬
      return a.subjectName.localeCompare(b.subjectName);
    });

    return sortedData;
  }

  return (
    <div>
      {isDataLoading && <LoadingModal />}
      <div>
        <div className="d-flex justify-content-end flex-wrap mt-2 align-items-center">
          <div style={{ width: "100px" }} className="me-3 text-end">
            <label htmlFor="searchKeyword" className="col-form-label text-end">
              {t("common.labels.search")}
            </label>
          </div>
          <div style={{ width: "150px" }} className="me-1">
            <input
              type="text"
              id="searchKeyword"
              name="searchKeyword"
              className="form-control"
              placeholder={t("common.user.studentName") || "학생이름"}
              value={searchKey}
              onChange={handleSearch}
            />
          </div>
          <div style={{ width: "150px" }} className="me-1">
            <select
              className="form-control"
              name="username"
              onChange={handleSelectUsername}
              value={filters.selectedStudent || ""}
            >
              <option value="">
                {t("teacher.printingRelated.selectUsername")}
              </option>
              {usernameList &&
                usernameList.map((user) => (
                  <option key={user.id} value={user.username}>
                    {user.username}
                  </option>
                ))}
            </select>
          </div>
          <div style={{ width: "70px" }} className="me-1 text-center">
            <button
              className="btn btn-success"
              type="button"
              onClick={() => {
                handlePrint(null, () => contentToPrint.current);
              }}
              disabled={isDataLoading}
            >
              {t("common.labels.print")}
            </button>
          </div>
          <div style={{ width: "100px" }} className="me-1 text-center">
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => setShowControlBar(!showControlBar)}
            >
              출력제어
            </button>
          </div>
        </div>
      </div>
      <div className={styles.transcriptWrapper} ref={contentToPrint}>
        {(schoolId === "haven" || schoolId === "sampleschool") && (
          <HavenTranscript
            keyArray={keyArray}
            gradeRows={gradeRows}
            attendanceRows={attendanceRows}
            settingData={settingData}
            displaySettings={displaySettings}
          />
        )}
      </div>
      {showControlBar && (
        <DisplayControlBar
          displaySettings={displaySettings}
          setDisplaySettings={setDisplaySettings}
          onClose={() => setShowControlBar(false)}
        />
      )}
    </div>
  );
}
